import { Injectable, isDevMode } from '@angular/core'
import { RxDatabase, RxJsonSchema, addRxPlugin, createRxDatabase, RxCollection } from 'rxdb'
import { wrappedValidateAjvStorage } from 'rxdb/plugins/validate-ajv'
import { RxDBAttachmentsPlugin } from 'rxdb/plugins/attachments'
import { RxDBDevModePlugin } from 'rxdb/plugins/dev-mode'
import { getRxStorageDexie } from 'rxdb/plugins/storage-dexie'
import { RxDBUpdatePlugin } from 'rxdb/plugins/update'
import { RxDBLeaderElectionPlugin } from 'rxdb/plugins/leader-election'
import { BehaviorSubject, from, map } from 'rxjs'

export enum DBCollection {
  LearningEvents = 'learning_event'
}
const RxDB_NAME = 'burraga-0'
const learningEventSchema: RxJsonSchema<any> | any = {
  title: 'learning event schema',
  version: 0,
  type: 'object',
  primaryKey: 'id',
  properties: {
    id: {
      type: 'string',
      maxLength: 100,
      primary: true,
    },
    date_created: {
      type: 'string',
      maxLength: 100
    },
    itemId: {
      type: 'string',
      maxLength: 100,
    },
    type: {
      type: 'string',
      maxLength: 50,
    },
    unit: {
      type: 'number',
      minimum: 1
    },
    level: {
      type: 'number',
      minimum: 1
    },
    sub_level: {
      type: 'number',
      minimum: 1
    },
    dictionary: {
      type: ['string', 'null'],
      maxLength: 100
    },
    game_type: {
      type: ['string', 'null'],
      maxLength: 50
    }
  },
  "required": ["id", "itemId", "type"]
} as const

async function loadRxDBPlugins(): Promise<void> {
  addRxPlugin(RxDBAttachmentsPlugin)
  addRxPlugin(RxDBUpdatePlugin)
  addRxPlugin(RxDBLeaderElectionPlugin)

  if (isDevMode()) {
    addRxPlugin(RxDBDevModePlugin)
  }
}

loadRxDBPlugins()

@Injectable({
  providedIn: 'root'
})
export class RxdbService {
  private rxDatabase!: RxDatabase
  private _dataBaseReady$ = new BehaviorSubject<boolean>(false);
  dataBaseReady$ = this._dataBaseReady$.asObservable();

  getDatabaseCollection(collectionName: string): RxCollection {
    if (!this.rxDatabase) {
      throw new Error(
        'Database is not initialized. Please make sure the database is initialized before getting the collection',
      )
    }
    return this.rxDatabase[collectionName]
  }

  async initDB(): Promise<RxDatabase> {
    const databaseName: string = RxDB_NAME

    if (this.rxDatabase && this.rxDatabase.name === databaseName && !this.rxDatabase.destroyed) {
      return this.rxDatabase
    }

    const storage = isDevMode()
      ? wrappedValidateAjvStorage({ storage: getRxStorageDexie() })
      : getRxStorageDexie()

    this.rxDatabase = await createRxDatabase({
      name: databaseName,
      storage
    })

    await this.createCollections()
    this._dataBaseReady$.next(true)

    return this.rxDatabase
  }

  async createCollections() {
    await this.rxDatabase.addCollections({
      [DBCollection.LearningEvents]: {
        schema: learningEventSchema
      },
    })
  }

  create(collection: RxCollection, body: any) {
    return from(collection.insert(body))
      .pipe(map((doc) => doc._data))
  }

  async getList(collection: RxCollection) {
    if (!collection) return

    return await collection.find().exec()
  }

  async clearCollection(collection: RxCollection) {
    await collection.remove()
  }
}
